'use client';

import { type CSSProperties, Fragment, type ReactNode } from 'react';

import { cn } from '@ps/ui/utils';

/**
 * Local node shape — a subset of `slate`'s `Descendant` union, kept
 * here so the renderer doesn't pull `slate` into the bundle just to
 * walk a JSON tree we already have on the wire. The server-stored
 * shape is documented in PopshopRN's
 * `channel-message-editor/custom-types.d.ts`.
 */
type SlateText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
};

type SlateElement = {
  type: string;
  children: SlateNode[];
  align?: string;
  url?: string;
  language?: string;
  checked?: boolean;
  profile?: {
    firstName?: string;
    lastName?: string;
    storeFrontName?: string;
  };
};

export type SlateNode = SlateText | SlateElement;

interface Props {
  nodes: SlateNode[];
  className?: string;
}

/**
 * Render a Slate-DOM JSON tree as React. Mirrors PopshopRN's
 * `SlateMessageRenderer` in shape — paragraphs, headings, lists,
 * block-quote, links, mentions, code blocks, tables, alignment,
 * inline marks. Unknown element types render their children inside a
 * `<div>` so messages from a future Slate schema don't disappear.
 *
 * Stays inert for inline `image` / `video` element types — slice 1
 * doesn't render media inline, and dedicated attachments take a
 * separate path. The fallback is an empty fragment so a stray
 * media element in a slate-DOM message doesn't blow up the bubble.
 */
export function SlateMessageRenderer({ nodes, className }: Props) {
  return (
    <div
      className={cn(
        'space-y-1 whitespace-pre-wrap break-words text-sm leading-snug',
        // shadcn-style spacing for child blocks. Specific selectors
        // here so we don't have to spread classNames across every
        // case-branch below.
        '[&_a]:text-brand-text [&_a]:hover:text-brand-hover [&_a]:underline',
        '[&_blockquote]:border-line-strong [&_blockquote]:text-content-2 [&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic',
        '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_code]:bg-surface-2 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs',
        '[&_pre]:bg-surface-1 [&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:p-2 [&_pre]:text-xs',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        '[&_table]:my-1 [&_table]:w-full [&_table]:border-collapse',
        '[&_td]:border-line [&_td]:border [&_td]:px-2 [&_td]:py-1',
        className,
      )}
    >
      {nodes.map((node, i) => (
        <Node key={i} node={node} />
      ))}
    </div>
  );
}

function Node({ node }: { node: SlateNode }) {
  if (isText(node)) return <TextLeaf leaf={node} />;
  return <Element element={node} />;
}

function TextLeaf({ leaf }: { leaf: SlateText }) {
  // Preserve empty text leaves (Slate fills them as cursor anchors).
  // Returning an empty fragment is safe and matches the PopshopRN
  // renderer's behavior.
  let node: ReactNode = leaf.text;
  if (leaf.code) node = <code>{node}</code>;
  if (leaf.bold) node = <strong>{node}</strong>;
  if (leaf.italic) node = <em>{node}</em>;
  if (leaf.underline) node = <u>{node}</u>;
  if (leaf.strikethrough) node = <s>{node}</s>;
  return <Fragment>{node}</Fragment>;
}

function Element({ element }: { element: SlateElement }) {
  const children = element.children.map((child, i) => (
    <Node key={i} node={child} />
  ));
  const alignStyle: CSSProperties | undefined = element.align
    ? { textAlign: element.align as CSSProperties['textAlign'] }
    : undefined;

  switch (element.type) {
    case 'paragraph':
      return <p style={alignStyle}>{children}</p>;
    case 'heading-one':
      return (
        <h1 style={alignStyle} className="text-base font-bold">
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={alignStyle} className="text-base font-semibold">
          {children}
        </h2>
      );
    case 'heading-three':
      return (
        <h3 style={alignStyle} className="text-sm font-semibold">
          {children}
        </h3>
      );
    case 'heading-four':
      return (
        <h4 style={alignStyle} className="text-sm font-semibold">
          {children}
        </h4>
      );
    case 'heading-five':
      return (
        <h5 style={alignStyle} className="text-sm font-semibold">
          {children}
        </h5>
      );
    case 'heading-six':
      return (
        <h6 style={alignStyle} className="text-sm font-semibold">
          {children}
        </h6>
      );
    case 'block-quote':
      return <blockquote style={alignStyle}>{children}</blockquote>;
    case 'bulleted-list':
      return <ul>{children}</ul>;
    case 'numbered-list':
      return <ol>{children}</ol>;
    case 'list-item':
      return <li>{children}</li>;
    case 'check-list-item':
      return (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={!!element.checked}
            readOnly
            className="mt-1"
          />
          <span
            className={
              element.checked ? 'text-content-3 line-through' : undefined
            }
          >
            {children}
          </span>
        </div>
      );
    case 'link':
      return (
        <a
          href={element.url}
          target="_blank"
          rel="noopener noreferrer"
          // Stop the click from bubbling into the bubble's wrapper
          // (which is currently inert, but future bubble behavior —
          // tap-to-reply, message focus, etc. — should not fire when
          // the user is clearly clicking a URL).
          onClick={event => event.stopPropagation()}
        >
          {children}
        </a>
      );
    case 'mention': {
      const displayName = nicknameWithInitial(element.profile);
      return (
        <span className="bg-brand/10 text-brand-text mx-0.5 inline-block rounded px-1 py-px text-[0.9em]">
          @{displayName}
        </span>
      );
    }
    case 'code-block':
      return (
        <pre>
          <code>{children}</code>
        </pre>
      );
    case 'code-line':
      return <div>{children}</div>;
    case 'table':
      return (
        <table>
          <tbody>{children}</tbody>
        </table>
      );
    case 'table-row':
      return <tr>{children}</tr>;
    case 'table-cell':
      return <td>{children}</td>;
    case 'title':
      return <h1 className="text-base font-bold">{children}</h1>;
    case 'image':
    case 'video':
    case 'button':
    case 'badge':
      // Slice 1 doesn't render inline media / button / badge nodes.
      // They're rare in chat messages — render their text children
      // so we don't silently drop content.
      return <Fragment>{children}</Fragment>;
    default:
      return <div>{children}</div>;
  }
}

function isText(node: SlateNode): node is SlateText {
  return typeof (node as { text?: unknown }).text === 'string';
}

/**
 * Mirror of PopshopRN's `nicknameWithInitial(profile)` — store name
 * wins, otherwise `First L.`, otherwise just first name. Used to
 * format `@mention` chips inside Slate content.
 */
function nicknameWithInitial(
  profile: SlateElement['profile'] | undefined,
): string {
  if (!profile) return 'mention';
  if (profile.storeFrontName) return profile.storeFrontName;
  const first = (profile.firstName ?? '').trim();
  const last = (profile.lastName ?? '').trim();
  if (last) return `${first} ${last.charAt(0).toUpperCase()}.`;
  return first || 'mention';
}
